import React from 'react';

/**
 * PanelLoadingSkeleton - A dark-themed loading skeleton for panel components
 * Matches the dark theme (#1e1e1e background) to prevent white flash during lazy loading
 */
const PanelLoadingSkeleton = ({ className = '' }) => {
  return (
    <div 
      className={`panel-loading-skeleton ${className}`}
      style={{
        width: '100%',
        height: '100%',
        backgroundColor: '#2d2d30', // VS Code tab bar background - matches panel background
        display: 'flex',
        flexDirection: 'column',
        padding: '1rem',
        color: '#cccccc', // VS Code inactive tab text
      }}
    >
      <div
        style={{
          width: '60%',
          height: '1.5rem',
          backgroundColor: '#37373d', // VS Code hover background - slightly lighter for skeleton
          borderRadius: '4px',
          marginBottom: '1rem',
          animation: 'pulse 1.5s ease-in-out infinite',
        }}
      />
      <div
        style={{
          width: '100%',
          height: '1rem',
          backgroundColor: '#37373d',
          borderRadius: '4px',
          marginBottom: '0.5rem',
          animation: 'pulse 1.5s ease-in-out infinite 0.2s',
        }}
      />
      <div
        style={{
          width: '90%',
          height: '1rem',
          backgroundColor: '#37373d',
          borderRadius: '4px',
          marginBottom: '0.5rem',
          animation: 'pulse 1.5s ease-in-out infinite 0.4s',
        }}
      />
      <div
        style={{
          width: '80%',
          height: '1rem',
          backgroundColor: '#37373d',
          borderRadius: '4px',
          animation: 'pulse 1.5s ease-in-out infinite 0.6s',
        }}
      />
      <style jsx>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
};

/**
 * NotesPanelSkeleton - Loading skeleton for Notes panel
 */
export const NotesPanelSkeleton = () => (
  <PanelLoadingSkeleton className="notes-panel-skeleton" />
);

/**
 * EntityInfoPanelSkeleton - Loading skeleton for Entity Info panel
 * Uses #1e1e1e background to match entityinfo-panel styles
 */
export const EntityInfoPanelSkeleton = () => (
  <div
    className="entityinfo-panel-skeleton"
    style={{
      width: '100%',
      height: '100%',
      backgroundColor: '#1e1e1e', // VS Code dark theme background - matches entityinfo-panel
      display: 'flex',
      flexDirection: 'column',
      padding: '1.5rem 1.25rem',
      color: '#cccccc', // VS Code inactive tab text
    }}
  >
    <div
      style={{
        width: '60%',
        height: '1.5rem',
        backgroundColor: '#37373d', // VS Code hover background
        borderRadius: '4px',
        marginBottom: '1.5rem',
        animation: 'pulse 1.5s ease-in-out infinite',
      }}
    />
    <div
      style={{
        width: '100%',
        height: '1rem',
        backgroundColor: '#37373d',
        borderRadius: '4px',
        marginBottom: '0.75rem',
        animation: 'pulse 1.5s ease-in-out infinite 0.2s',
      }}
    />
    <div
      style={{
        width: '90%',
        height: '1rem',
        backgroundColor: '#37373d',
        borderRadius: '4px',
        marginBottom: '0.75rem',
        animation: 'pulse 1.5s ease-in-out infinite 0.4s',
      }}
    />
    <div
      style={{
        width: '80%',
        height: '1rem',
        backgroundColor: '#37373d',
        borderRadius: '4px',
        animation: 'pulse 1.5s ease-in-out infinite 0.6s',
      }}
    />
    <style jsx>{`
      @keyframes pulse {
        0%, 100% {
          opacity: 1;
        }
        50% {
          opacity: 0.5;
        }
      }
    `}</style>
  </div>
);

/**
 * NotebookPanelSkeleton - Loading skeleton for Notebook panel
 */
export const NotebookPanelSkeleton = () => (
  <PanelLoadingSkeleton className="notebook-panel-skeleton" />
);

/**
 * AOFCollectionsSkeleton - Loading skeleton for Areas of Focus panel
 */
export const AOFCollectionsSkeleton = () => (
  <PanelLoadingSkeleton className="aof-collections-skeleton" />
);

/**
 * EditorWrapperSkeleton - Loading skeleton for Editor wrapper
 */
export const EditorWrapperSkeleton = () => (
  <div
    className="editor-wrapper-skeleton"
    style={{
      width: '100%',
      height: '100%',
      backgroundColor: '#1e1e1e', // VS Code dark theme background
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <div
      style={{
        width: '100%',
        height: '3rem',
        backgroundColor: '#252526', // Slightly lighter background for header
        borderBottom: '1px solid #3c3c3c', // VS Code tab border
      }}
    />
    <div
      style={{
        flex: 1,
        padding: '2rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        backgroundColor: '#1e1e1e',
      }}
    >
      <div
        style={{
          width: '40%',
          height: '2rem',
          backgroundColor: '#37373d', // VS Code hover background
          borderRadius: '4px',
          animation: 'pulse 1.5s ease-in-out infinite',
        }}
      />
      <div
        style={{
          width: '100%',
          height: '1rem',
          backgroundColor: '#37373d',
          borderRadius: '4px',
          animation: 'pulse 1.5s ease-in-out infinite 0.2s',
        }}
      />
      <div
        style={{
          width: '100%',
          height: '1rem',
          backgroundColor: '#37373d',
          borderRadius: '4px',
          animation: 'pulse 1.5s ease-in-out infinite 0.4s',
        }}
      />
    </div>
    <style jsx>{`
      @keyframes pulse {
        0%, 100% {
          opacity: 1;
        }
        50% {
          opacity: 0.5;
        }
      }
    `}</style>
  </div>
);

/**
 * OpenNotesPanelSkeleton - Loading skeleton for Open Notes Panel
 */
export const OpenNotesPanelSkeleton = () => (
  <PanelLoadingSkeleton className="open-notes-panel-skeleton" />
);

/**
 * NoteHistoryPanelSkeleton - Loading skeleton for Note History panel
 */
export const NoteHistoryPanelSkeleton = () => (
  <PanelLoadingSkeleton className="note-history-panel-skeleton" />
);

export default PanelLoadingSkeleton;

